/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractFormComponent, FormFlowState } from '@gh/builder-shared';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  ContainerState,
  DEFAULT_AUTOSAVE_INTERVALS,
  getRouteInitData,
  getRouteQueryParam,
  ImmutablePageState,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  queryRecalculationNeeded,
  State,
} from '@gh/common-shared';
import { getLocalValue, openInNewWindow, setLocalValue } from '@gh/core-util';
import { map$, skip$ } from '@gh/rx/operators';
import { Observable, Subscription } from 'rxjs';
import { isHpeComponentCostCenter } from '../../resources/hpe-details.model';
import { HpeCommandService } from './hpe-command.service';
import {
  HPE_DETAILS_INIT_STATE,
  HpeDetailsMasterInfo,
  HpeDetailsState,
  queryHpeCost,
  queryHpeEditable,
  queryHpeMasterInfo,
  queryHpeSelectedCostCenter,
} from './hpe-details.state';
import { hpeDetailsReducer } from './reducers/hpe-details.reducers';

const HPE_AUTOSAVE_LOCAL_KEY = 'hpe:autosave';

export function hpeDetailsStateFactory(): State<HpeDetailsState> {
  return new State(HPE_DETAILS_INIT_STATE, hpeDetailsReducer);
}

export function hpeDetailsFormFactory(state: State<HpeDetailsState>): ImmutablePageState<HpeDetailsState> {
  return new ImmutablePageState(state, {
    dirty: queryDirty,
    formMode: queryFormMode,
  });
}

@Component({
  standalone: false,
  selector: 'gh-house-plan-estimate-details',
  templateUrl: 'house-plan-estimate-details.component.html',
  styleUrls: ['house-plan-estimate-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: hpeDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: hpeDetailsFormFactory, deps: [State] },
    HpeCommandService,
  ],
})
export class HousePlanEstimateDetailsComponent extends AbstractFormComponent<HpeDetailsState> implements OnInit, OnDestroy {
  parentTitle: string;
  cost$: Observable<number>;
  recalculationNeeded$: Observable<boolean>;
  dirty$: Observable<boolean>;
  isComponentCostCenter$: Observable<boolean>;
  isTypicalCostCenter$: Observable<boolean>;
  editable: boolean;
  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(HPE_AUTOSAVE_LOCAL_KEY, '30m');
  autoSaveActivityTick = 0;
  reportUrl: string;
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;
  private autoSaveActivitySubscription?: Subscription;

  constructor(private route: ActivatedRoute,
              private state: State<HpeDetailsState>,
              private commands: HpeCommandService,
              private changeDetectorRef: ChangeDetectorRef,
              @Inject(PAGE_STATE) public form: ContainerState<HpeDetailsState>) {
    super();
  }

  get masterInfo(): HpeDetailsMasterInfo {
    return this.state.project(queryHpeMasterInfo);
  }

  get completeFlow(): ActionFlow<FormFlowState<HpeDetailsState>> {
    return this.commands.completeFlow();
  }

  ngOnInit(): void {
    const { details, reportUrl } = getRouteInitData(this.route);

    this.commands.load(details);

    this.reportUrl = reportUrl;

    this.parentTitle = getRouteQueryParam(this.route, 'parentTitle');
    this.cost$ = this.state.select(queryHpeCost);
    this.recalculationNeeded$ = this.state.select(queryRecalculationNeeded);
    this.dirty$ = this.state.select(queryDirty);

    this.editable = this.state.project(queryHpeEditable);

    const costCenter$ = this.state.select(queryHpeSelectedCostCenter);
    this.isComponentCostCenter$ = costCenter$.pipe(map$((cc) => cc && isHpeComponentCostCenter(cc) || false));
    this.isTypicalCostCenter$ = costCenter$.pipe(map$((cc) => cc && !isHpeComponentCostCenter(cc) || false));

    this.setHistoryObservable();
    this.autoSaveActivitySubscription = this.state.pipe(skip$(1)).subscribe(() => {
      if (this.state.project(queryDirty)) {
        this.autoSaveActivityTick++;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.autoSaveActivitySubscription?.unsubscribe();
  }

  setHistoryObservable(): void {
    this.commands.initHistory();
    this.isUndoDisabled$ = this.commands.isUndoDisabled$;
    this.isRedoDisabled$ = this.commands.isRedoDisabled$;
  }
  undo(): void {
    this.commands.undo();
  }
  redo(): void {
    this.commands.redo();
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(HPE_AUTOSAVE_LOCAL_KEY, interval);
  }

  onAutoSave(event: TimerEvent): void {
    event.waitFor(this.commands.autoSave());
  }

  onRecalculate(): void {
    this.commands.recalculate();
  }

  onProcess(): void {
    this.commands.process();
  }

  onOpenUsedBy(): void {
    this.commands.openUsedBy();
  }

  onOpenReportBom(): void {
    openInNewWindow(this.reportUrl);
  }
}
